import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  BaseResponseDTO,
  FuelTankEntity,
  GeneratorSearch,
  GeneratorSearchDTO,
  TelemetryActionRequest
} from '@psnb/psnb-api';
import { AuthService, GeneratorService, isDefinedNotNull, isDefinedNotNullNotEmpty, isUndefinedOrNull } from '@psnb/services';
import { distinctUntilChanged, Subject } from 'rxjs';
import { debounceTime, take, takeUntil } from 'rxjs/operators';
import { PowerSupplyMessageDialog } from '../../../common/message-dialog/power-supply-message-dialog';
import { DeployGeneratorViewModel } from '../telemetry-generator.model';
import { QrScannerDialogComponent } from './qr-scanner-dialog.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'psnb-generator-search-dialog',
  styleUrls: ['./deploy-generator-dialog.component.scss'],
  templateUrl: './deploy-generator-dialog.component.html'
})
export class DeployGeneratorDialogComponent implements OnInit, OnDestroy {
  // All public model properties are now in vm
  public vm: DeployGeneratorViewModel;

  // ViewChild references for autocomplete triggers
  @ViewChild('fuelTankIDAutoTrigger', { read: MatAutocompleteTrigger }) private fuelTankAutocompleteTrigger: MatAutocompleteTrigger;
  @ViewChild('generatorIDAutoTrigger', { read: MatAutocompleteTrigger }) private generatorAutocompleteTrigger: MatAutocompleteTrigger;

  // Private, readonly variables
  private readonly destroyed$: Subject<void>;
  private readonly slowDebounceTimeInMs: number = 750;

  // Private variables
  private createdTimeValue: string = new Date().toISOString();
  private extendedRuntimeFuelTank: string;
  private fuelTankDataResponse: FuelTankEntity;
  private generatorDataResponse: GeneratorSearchDTO;
  private powerSupplyId: number;
  private powerSupplyMessageDialogRef: MatDialogRef<PowerSupplyMessageDialog> | undefined;
  private qrScannerDialogRef: MatDialogRef<QrScannerDialogComponent> | undefined;
  private selectedFuelTankId: string;
  private selectedGeneratorId: string;
  private userName: string;

  public constructor(
    private authService: AuthService,
    private readonly cdr: ChangeDetectorRef,
    private deployGeneratorComponentDialogRef: MatDialogRef<DeployGeneratorDialogComponent>,
    private readonly dialog: MatDialog,
    private generatorService: GeneratorService,
    @Inject(MAT_DIALOG_DATA) private powerSupplyData: { powerSupplyId: number }
  ) {
    this.destroyed$ = new Subject<void>();
  }

  public ngOnInit(): void {
    this.vm = {
      comments: '',
      deployGeneratorButtonDisabled: true,
      formGroup: this.createFormGroup(),
      fuelTankAssetSearchResult: [],
      generatorAssetSearchResult: [],
      serialNumberSearchResult: []
    };

    this.powerSupplyId = this.powerSupplyData.powerSupplyId;
    this.userName = `${this.authService.getUserDetails.firstName} ${this.authService.getUserDetails.lastName}`;

    this.setupSearchSubscription(
      this.vm.formGroup.get('generatorIDSearchTerm'),
      (generatorData: string | GeneratorSearch): void => {
        if (typeof generatorData === 'string' && isDefinedNotNullNotEmpty(generatorData)) {
          this.generatorService
            .advanceSearchByAssetID(generatorData)
            .pipe(take(1), takeUntil(this.destroyed$))
            .subscribe((response: GeneratorSearch[]): void => {
              this.vm.generatorAssetSearchResult = response.filter(
                (item: GeneratorSearch): boolean => isDefinedNotNull(item.generatorId) && item.assetType === 'Generator'
              );
              this.cdr.detectChanges();
              // Automatically open the autocomplete panel if there are results
              if (this.vm.generatorAssetSearchResult.length > 0 && this.generatorAutocompleteTrigger) {
                this.generatorAutocompleteTrigger.openPanel();
              }
            });
        } else if (isUndefinedOrNull(generatorData) || typeof generatorData === 'string') {
          // generatorData is undefined, or null, or an empty string, or 1 character in length
          this.vm.generatorAssetSearchResult = [];
          this.cdr.detectChanges();
          this.resetDeployButton();
        } /* Otherwise, generatorData is aGeneratorSearch due to the user picking an autocomplete option and will be
             handled in the methods tied to the (optionSelected) in the HTML */
      }
    );

    this.setupSearchSubscription(this.vm.formGroup.get(
      'fuelTankIDSearchTerm'),
      (fuelTankData: string | GeneratorSearch): void => {
        if (typeof fuelTankData === 'string' && isDefinedNotNullNotEmpty(fuelTankData)) {
          this.generatorService
            .advanceSearchByAssetID(fuelTankData)
            .pipe(take(1), takeUntil(this.destroyed$))
            .subscribe((response: GeneratorSearch[]): void => {
              this.vm.fuelTankAssetSearchResult = response.filter(
                (item: GeneratorSearch): boolean => isDefinedNotNull(item.fuelTankId) && item.assetType === 'Fuel Tank'
              );
              this.cdr.detectChanges();
              // Automatically open the autocomplete panel if there are results
              if (this.vm.fuelTankAssetSearchResult.length > 0 && this.fuelTankAutocompleteTrigger) {
                this.fuelTankAutocompleteTrigger.openPanel();
              }
            });
        } /* No need to handle other cases */
      }
    );

    this.setupSearchSubscription(this.vm.formGroup.get(
      'serialNumberSearchTerm'),
      (serialNumberData: string | GeneratorSearch): void => {
        if (typeof serialNumberData === 'string' && isDefinedNotNullNotEmpty(serialNumberData)) {
          this.generatorService
            .generatorSearchBySerialNumber(serialNumberData)
            .pipe(take(1), takeUntil(this.destroyed$))
            .subscribe((response: GeneratorSearch[]): void => {
              this.vm.serialNumberSearchResult = response;
              this.cdr.detectChanges();
            });
        } else if (isUndefinedOrNull(serialNumberData) || typeof serialNumberData === 'string') {
          // serialNumberData is undefined, or null, or an empty string, or 1 character in length
          this.vm.serialNumberSearchResult = [];
          this.cdr.detectChanges();
          this.resetDeployButton();
        } /* Otherwise, generatorData is aGeneratorSearch due to the user picking an autocomplete option and will be
             handled in the methods tied to the (optionSelected) in the HTML */
      }
    );
  }

  /**
   * Angular lifecycle hook called when the component is destroyed.
   * Closes the dialog and cleans up subscriptions.
   */
  public ngOnDestroy(): void {
    if (
      isDefinedNotNull(this.deployGeneratorComponentDialogRef?.getState) &&
      this.deployGeneratorComponentDialogRef.getState() !== 0
    ) {
      /* empty */ // TODO (future): Condition needs to be revisited
    } else {
      this.deployGeneratorComponentDialogRef?.close(false);
    }
    this.powerSupplyMessageDialogRef?.close();
    this.qrScannerDialogRef?.close();
    this.destroyed$?.next();
    this.destroyed$?.complete();
  }

  /**
   * Gets the asset ID from a GeneratorSearch option.
   * Returns assetId, localId, or a stringified generatorId/fuelTankId in order of precedence.
   *
   * @param {GeneratorSearch | string} option - The generator search option or a string value
   * @returns The asset ID or undefined
   */
  public getAssetID(option: GeneratorSearch | string): string | undefined {
    // If option is already a string, return it directly
    if (typeof option === 'string') {
      return option;
    }

    // Otherwise, extract the ID from the GeneratorSearch object
    if (isDefinedNotNull(option?.assetId)) {
      return option.assetId;
    } else if (isDefinedNotNull(option?.localId)) {
      return option.localId;
    } else {
      return option?.generatorId?.toString() ?? option?.fuelTankId?.toString();
    }
  }

  /**
   * Gets the serial number from a GeneratorSearchDTO option.
   *
   * @param {GeneratorSearchDTO | string} option - The generator search DTO or a string value
   * @returns The generator serial number or undefined
   */
  public getSerialNumber(option: GeneratorSearchDTO | string): string | undefined {
    // If option is already a string, return it directly
    if (typeof option === 'string') {
      return option;
    }

    return option?.generatorSerialNumber;
  }

  private createFormGroup(): FormGroup {
    return new FormGroup({
      comments: new FormControl(''),
      fuelTankIDSearchTerm: new FormControl(undefined),
      generatorIDSearchTerm: new FormControl(undefined),
      serialNumberSearchTerm: new FormControl(undefined)
    });
  }

  private resetDeployButton(): void {
    this.generatorDataResponse = undefined;
    this.vm.deployGeneratorButtonDisabled = true;
    this.cdr.detectChanges();
  }

  /**
   * Handles the cancel action.
   * Closes the dialog with a false result.
   */
  public onCancel(): void {
    this.deployGeneratorComponentDialogRef.close(false);
  }

  /**
   * Deploys the selected generator.
   * Validates that a generator is selected, builds the payload, and calls the generator service.
   * Shows success or error dialogs based on the API response.
   */
  public deployGenerator(): void {
    if (isUndefinedOrNull(this.selectedGeneratorId)) {
      if (isUndefinedOrNull(this.powerSupplyMessageDialogRef)) {
        this.powerSupplyMessageDialogRef = this.dialog.open(PowerSupplyMessageDialog, {
          data: { message: 'Please select a Generator before confirming.' }
        });
        this.powerSupplyMessageDialogRef
          .afterClosed()
          .pipe(take(1), takeUntil(this.destroyed$))
          .subscribe((): void => {
            this.powerSupplyMessageDialogRef = undefined;
          });
      }
      return;
    }

    const payload: TelemetryActionRequest = {
      address: this.generatorDataResponse?.address,
      city: this.generatorDataResponse?.city,
      comments: this.vm.formGroup.get('comments')?.value,
      createDate: this.createdTimeValue,
      createdByUserId: this.userName,
      currentType: this.generatorDataResponse?.currentType,
      division: this.generatorDataResponse?.division,
      extendedRuntimeFuelTank: this.extendedRuntimeFuelTank,
      fuelTankId: this.selectedFuelTankId,
      generatorChain: this.generatorDataResponse?.generatorChain,
      generatorCord: this.generatorDataResponse?.generatorCord,
      generatorId: this.selectedGeneratorId,
      generatorLastModified: this.createdTimeValue,
      generatorLastModifiedBy: this.userName,
      generatorLock: this.generatorDataResponse?.generatorLock,
      generatorManufactureId: this.generatorDataResponse?.generatorManufactureId,
      generatorManufacturer: this.generatorDataResponse?.generatorManufacturer,
      generatorModel: this.generatorDataResponse?.generatorModel,
      generatorModelId: this.generatorDataResponse?.generatorModelId,
      generatorRuntimeHours: this.generatorDataResponse?.generatorRuntimeHours,
      generatorSerialNumber: this.generatorDataResponse?.generatorSerialNumber,
      generatorStatus: this.generatorDataResponse?.generatorStatus,
      generatorStatusId: this.generatorDataResponse?.generatorStatusId,
      latitude: this.generatorDataResponse?.latitude,
      loanedTeamName: this.generatorDataResponse?.loanedTeamName,
      localId: this.generatorDataResponse?.localId,
      longitude: this.generatorDataResponse?.longitude,
      outputVoltage: this.generatorDataResponse?.outputVoltage,
      powerSupplyId: this.powerSupplyId,
      qrCodeId: this.generatorDataResponse?.qrCodeId,
      region: this.generatorDataResponse?.region,
      state: this.generatorDataResponse?.state,
      teamName: this.generatorDataResponse?.teamName,
      zipCode: this.generatorDataResponse?.zipcode
    };
    this.generatorService
      .deployGenerator(payload)
      .pipe(take(1), takeUntil(this.destroyed$))
      .subscribe({
        next: (deployResponse: BaseResponseDTO): void => {
          if (deployResponse?.success) {
            if (isUndefinedOrNull(this.powerSupplyMessageDialogRef)) {
              this.powerSupplyMessageDialogRef = this.dialog.open(PowerSupplyMessageDialog, {
                data: { message: 'Generator deployed successfully!' }
              });
              this.powerSupplyMessageDialogRef
                .afterClosed()
                .pipe(take(1), takeUntil(this.destroyed$))
                .subscribe((): void => {
                  this.powerSupplyMessageDialogRef = undefined;
                  if (deployResponse?.success) {
                    this.deployGeneratorComponentDialogRef.close(true);
                  }
                });
            }
          } else if (!deployResponse?.success) {
            const errorMessage: string = deployResponse?.message ?? 'Failed to deploy generator. Please try again.';
            if (isUndefinedOrNull(this.powerSupplyMessageDialogRef)) {
              this.powerSupplyMessageDialogRef = this.dialog.open(PowerSupplyMessageDialog, {
                data: { message: errorMessage }
              });
              this.powerSupplyMessageDialogRef
                .afterClosed().pipe(take(1), takeUntil(this.destroyed$))
                .subscribe((): void => {
                  this.powerSupplyMessageDialogRef = undefined;
                });
            }
          }
        },
        error: (): void => { // eslint-disable-line sort-keys
          if (isUndefinedOrNull(this.powerSupplyMessageDialogRef)) {
            this.powerSupplyMessageDialogRef = this.dialog.open(PowerSupplyMessageDialog, {
              data: { message: 'Failed to deploy generator. Please try again.' }
            });
            this.powerSupplyMessageDialogRef
              .afterClosed().pipe(take(1), takeUntil(this.destroyed$))
              .subscribe((): void => {
                this.powerSupplyMessageDialogRef = undefined;
              });
          }
        }
      });
  }

  /**
   * Handles generator selection from the autocomplete.
   * Validates the selected option is a generator and fetches its data.
   *
   * @param {MatAutocompleteSelectedEvent} event - The autocomplete selection event
   */
  public searchByGeneratorId(event: MatAutocompleteSelectedEvent): void {
    if (isDefinedNotNull(event.option?.value?.fuelTankId) || event.option.value.fuelTankId === 0 ||
        isDefinedNotNull(event.option?.value?.trailerId) || event.option.value.trailerId === 0) {
      if (isUndefinedOrNull(this.powerSupplyMessageDialogRef)) {
        this.powerSupplyMessageDialogRef = this.dialog.open(PowerSupplyMessageDialog, {
          data: { message: 'please select a Generator' }
        });
        this.powerSupplyMessageDialogRef
          .afterClosed()
          .pipe(take(1), takeUntil(this.destroyed$))
          .subscribe((): void => {
            this.powerSupplyMessageDialogRef = undefined;
          });
      }
      return;
    } else {
      this.selectedGeneratorId = event.option.value.generatorId;
      // Clear Serial Number selection
      this.vm.formGroup.get('serialNumberSearchTerm')
        .setValue('', { emitEvent: false, onlySelf: true });
      this.cdr.detectChanges();
      this.fetchGeneratorDataById(this.selectedGeneratorId);
    }
  }

  /**
   * Handles fuel tank selection from the autocomplete.
   * Validates the selected option is a fuel tank and fetches its data.
   *
   * @param {MatAutocompleteSelectedEvent} event - The autocomplete selection event
   */
  public searchByFuelTankId(event: MatAutocompleteSelectedEvent): void {
    if (event.option.value.generatorId || event.option.value.trailerId) {
      if (isUndefinedOrNull(this.powerSupplyMessageDialogRef)) {
        this.powerSupplyMessageDialogRef = this.dialog.open(PowerSupplyMessageDialog, {
          data: { message: 'please select a Fuel Tank' }
        });
        this.powerSupplyMessageDialogRef
          .afterClosed()
          .pipe(take(1), takeUntil(this.destroyed$))
          .subscribe((): void => {
            this.powerSupplyMessageDialogRef = undefined;
          });
      }
      return;
    } else {
      this.selectedFuelTankId = event.option.value.fuelTankId;
    }
    this.fetchFuelTankDataById(this.selectedFuelTankId);
  }

  /**
   * Handles generator selection by serial number from the autocomplete.
   * Clears the generator ID search field and fetches generator data.
   *
   * @param {MatAutocompleteSelectedEvent} event - The autocomplete selection event
   */
  public searchBySerialNumber(event: MatAutocompleteSelectedEvent): void {
    this.selectedGeneratorId = event.option.value.generatorId;
    // Clear QR Code Asset Id - Generator selection
    this.vm.formGroup.get('generatorIDSearchTerm')
      .setValue('', { emitEvent: false, onlySelf: true });
    this.cdr.detectChanges();
    this.fetchGeneratorDataById(this.selectedGeneratorId);
  }

  private fetchGeneratorDataById(generatorId: string): void {
    this.generatorService
      .generatorSearchByAssetId(generatorId)
      .pipe(take(1), takeUntil(this.destroyed$))
      .subscribe({
        next: (generatorResponse: GeneratorSearchDTO[]): void => {
          this.vm.deployGeneratorButtonDisabled = false;
          this.generatorDataResponse = generatorResponse[0];
          this.cdr.detectChanges();
        },
        error: (): void => { // eslint-disable-line sort-keys
          if (isUndefinedOrNull(this.powerSupplyMessageDialogRef)) {
            this.powerSupplyMessageDialogRef = this.dialog.open(PowerSupplyMessageDialog, {
              data: { message: 'Error retrieving generator data' }
            });
            this.powerSupplyMessageDialogRef
              .afterClosed()
              .pipe(take(1), takeUntil(this.destroyed$))
              .subscribe((): void => {
                this.powerSupplyMessageDialogRef = undefined;
              });
          }
        }
      });
  }

  private fetchFuelTankDataById(fuelTankId: string): void {
    this.generatorService
      .fuelTankSearchById(fuelTankId)
      .pipe(take(1), takeUntil(this.destroyed$))
      .subscribe({
        next: (fuelTankResponse: FuelTankEntity[]): void => {
          this.fuelTankDataResponse = fuelTankResponse[0];
          this.extendedRuntimeFuelTank = this.fuelTankDataResponse.capacity;
        },
        error: (): void => { // eslint-disable-line sort-keys
          if (isUndefinedOrNull(this.powerSupplyMessageDialogRef)) {
            this.powerSupplyMessageDialogRef = this.dialog.open(PowerSupplyMessageDialog, {
              data: { message: 'Error retrieving fuel tank data' }
            });
            this.powerSupplyMessageDialogRef
              .afterClosed()
              .pipe(take(1), takeUntil(this.destroyed$))
              .subscribe((): void => {
                this.powerSupplyMessageDialogRef = undefined;
              });
          }
        }
      });
  }

  private setupSearchSubscription(control: AbstractControl, searchFn: (searchResultData: string) => void): void {
    control.valueChanges
      .pipe(
        debounceTime(this.slowDebounceTimeInMs),
        distinctUntilChanged(),
        takeUntil(this.destroyed$)
      )
      .subscribe((searchSubscriptionData: string): void => {
        if (isDefinedNotNull(searchSubscriptionData)) {
          searchFn(searchSubscriptionData);
        }
      });
  }

  /**
   * TrackBy function for generator list to optimize change detection.
   *
   * @param {number} index - The index of the item
   * @param {GeneratorSearch} asset - The generator search asset
   * @returns The asset ID for tracking, or index as fallback
   */
  public trackByGenerator(index: number, asset: GeneratorSearch): string | number {
    return asset?.assetId ?? index;
  }

  /**
   * TrackBy function for fuel tank list to optimize change detection.
   *
   * @param {number} index - The index of the item
   * @param {GeneratorSearch} asset - The fuel tank search asset
   * @returns The asset ID for tracking, or index as fallback
   */
  public trackByFuelTank(index: number, asset: GeneratorSearch): string | number {
    return asset?.assetId ?? index;
  }

  /**
   * TrackBy function for serial number list to optimize change detection.
   *
   * @param {number} index - The index of the item
   * @param {GeneratorSearchDTO} serialNumber - The generator search DTO
   * @returns The generator serial number for tracking, or index as fallback
   */
  public trackBySerialNumber(index: number, serialNumber: GeneratorSearchDTO): string | number {
    return serialNumber?.generatorSerialNumber ?? index;
  }

  /**
   * Opens QR scanner dialog for generator asset ID.
   * Sets the scanned value to the generator search field when successful.
   */
  public openGeneratorScanner(): void {
    // Reset generator search results and form field
    this.vm.generatorAssetSearchResult = [];
    this.vm.formGroup.get('generatorIDSearchTerm')?.setValue(undefined);
    this.vm.deployGeneratorButtonDisabled = true;
    this.cdr.detectChanges();

    if (isUndefinedOrNull(this.qrScannerDialogRef)) {
      this.qrScannerDialogRef = this.dialog.open(QrScannerDialogComponent, {
        data: { title: 'Scan Generator QR Code' },
        disableClose: false,
        width: '500px'
      });

      this.qrScannerDialogRef
        .afterClosed()
        .pipe(take(1), takeUntil(this.destroyed$))
        .subscribe((scannedValue: string): void => {
          this.qrScannerDialogRef = undefined;
          if (isDefinedNotNull(scannedValue) && scannedValue !== '') {
            const generatorControl: AbstractControl | null = this.vm.formGroup.get('generatorIDSearchTerm');
            if (isDefinedNotNull(generatorControl)) {
              generatorControl.setValue(scannedValue);
              this.cdr.detectChanges();
            }
          }
        });
    }
  }

  /**
   * Opens QR scanner dialog for fuel tank asset ID.
   * Sets the scanned value to the fuel tank search field when successful.
   */
  public openFuelTankScanner(): void {
    // Reset fuel tank search results and form field
    this.vm.fuelTankAssetSearchResult = [];
    this.vm.formGroup.get('fuelTankIDSearchTerm')?.setValue(undefined);
    this.vm.deployGeneratorButtonDisabled = true;
    this.cdr.detectChanges();

    if (isUndefinedOrNull(this.qrScannerDialogRef)) {
      this.qrScannerDialogRef = this.dialog.open(QrScannerDialogComponent, {
        data: { title: 'Scan Fuel Tank QR Code' },
        disableClose: false,
        width: '500px'
      });

      this.qrScannerDialogRef
        .afterClosed()
        .pipe(take(1), takeUntil(this.destroyed$))
        .subscribe((scannedValue: string): void => {
          this.qrScannerDialogRef = undefined;
          if (isDefinedNotNull(scannedValue) && scannedValue !== '') {
            const fuelTankControl: AbstractControl | null = this.vm.formGroup.get('fuelTankIDSearchTerm');
            if (isDefinedNotNull(fuelTankControl)) {
              fuelTankControl.setValue(scannedValue);
              this.cdr.detectChanges();
            }
          }
        });
    }
  }
}
